import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { ModernSidebar } from "./Sidebar";
import { useActivityTracker } from "@/hooks/useActivityTracker";
import {
  SidebarProvider,
  SidebarInset,
  useSidebar,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { SideRailProvider, useSideRail } from "@/contexts/SideRailContext";
import { AskHobbesChat } from "@/components/dashboard/AskHobbesChat";
import { AnimatePresence, motion } from "framer-motion";
import { MOBILE_BREAKPOINT, useIsMobile } from "@/hooks/use-mobile";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { TopNavigation } from "@/components/layout/TopNavigation";

interface LayoutProps {
  children: React.ReactNode;
}

const pageVariants = {
  initial: { opacity: 0, y: 6 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -2 },
};

const pageTransition = {
  duration: 0.15,
  ease: [0.25, 0.1, 0.25, 1] as const,
};

function LayoutContent({ children }: LayoutProps) {
  const { isOpen, openSideRail, closeSideRail } = useSideRail();
  const { setOpen } = useSidebar();
  const isMobile = useIsMobile();
  const location = useLocation();

  // Auto-collapse sidebar when Ask Hobbes side rail opens (only on smaller screens)
  useEffect(() => {
    if (isOpen && typeof window !== "undefined" && window.innerWidth < MOBILE_BREAKPOINT) {
      setOpen(false);
    }
  }, [isOpen, setOpen]);

  const handleSideRailChange = (open: boolean) => {
    if (open) {
      openSideRail();
    } else {
      closeSideRail();
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-2 overflow-hidden p-2 sm:p-2.5 md:p-3">
      <div className="sticky top-0 z-30 -mx-2 flex items-center justify-between border-b bg-background/95 px-2 py-2 backdrop-blur sm:-mx-3 sm:px-3 md:hidden">
        <div className="flex items-center gap-2 min-w-0">
          <SidebarTrigger className="h-9 w-9" />
        </div>
        <TopNavigation className="gap-2" />
      </div>

      <div className="flex min-h-0 flex-1 gap-4 overflow-hidden">
        <div className="flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-hidden">
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={pageTransition}
              className="h-full"
            >
              {children}
            </motion.div>
          </AnimatePresence>
        </div>
        {!isMobile && (
          <AnimatePresence mode="wait">
            {isOpen && <AskHobbesChat />}
          </AnimatePresence>
        )}
      </div>

      {isMobile && (
        <Sheet open={isOpen} onOpenChange={handleSideRailChange}>
          <SheetContent
            side="right"
            className="h-full w-full p-0 [&>button]:hidden"
          >
            <AskHobbesChat />
          </SheetContent>
        </Sheet>
      )}
    </div>
  );
}

export function Layout({ children }: LayoutProps) {
  // Enable activity tracking for session extension
  useActivityTracker();

  return (
    <SideRailProvider>
      <SidebarProvider>
        <ModernSidebar />
        <SidebarInset>
          <LayoutContent>{children}</LayoutContent>
        </SidebarInset>
      </SidebarProvider>
    </SideRailProvider>
  );
}
